import { logError } from '@/lib/sentry.lib';

import { useAppStore } from '@/store/app.store';
import { EMPTY_PENDING_SUBMISSIONS, useAssetEditStore } from '@/store/asset-edit.store';
import Button from '@ingka/button';
import Modal, { ModalBody, ModalFooter, ModalHeader, Sheets } from '@ingka/modal';
import Text from '@ingka/text';
import { useOrderUpdateMutation } from '@repo/hooks';
import { translate } from '@repo/phrase';
import { isOrder } from '@repo/types/order';
import { getNotifier } from '@repo/utils';

import { useAssetPendingChangesGuard } from '@/hooks/asset-pending-changes-navigation.hooks';
import { useAssetServices } from '@/hooks/asset-services.hooks';
import { useAsset, useAssetParams } from '@/hooks/asset.hooks';

import { buildOrderUpdateCommandSummary } from './asset-review-changes-modal.utils';

import styles from './asset-review-changes-modal.module.scss';

interface Props {
    readonly visible: boolean;
    readonly onClose: () => void;
}

export const AssetReviewChangesModal = ({ visible, onClose }: Props) => {
    const { assetId, assetType } = useAssetParams();
    const { data: assetData } = useAsset(assetType, assetId, { includePendingChanges: false });
    const baselineServices = useAssetServices(assetData);
    const orderUpdateMutation = useOrderUpdateMutation();
    const guardPendingChanges = useAssetPendingChangesGuard();
    const exitAssetDetailsEditMode = useAppStore((state) => state.exitAssetDetailsEditMode);
    const pendingSubmissions = useAssetEditStore((state) =>
        state.assetId === assetId ? state.pendingSubmissions : EMPTY_PENDING_SUBMISSIONS
    );
    const clearPendingSubmissions = useAssetEditStore((state) => state.clearPendingSubmissions);
    const orderData = isOrder(assetData) ? assetData : undefined;
    const pendingSubmissionSummaries = orderData
        ? pendingSubmissions.flatMap((pendingSubmission) => {
              const summary = buildOrderUpdateCommandSummary({
                  orderData,
                  services: baselineServices,
                  command: pendingSubmission.submission.payload,
              });

              return summary ? [{ id: pendingSubmission.id, ...summary }] : [];
          })
        : [];
    const title = translate('asset_details_mode_review_changes');
    const orderId = orderData?.assetDetails.orderNo;
    const pendingOrderUpdateCommands = pendingSubmissions.map(
        (pendingSubmission) => pendingSubmission.submission.payload
    );

    const handleDiscard = () => {
        guardPendingChanges(onClose, 'discard');
    };

    const submitPendingChanges = () => {
        if (!orderId || pendingOrderUpdateCommands.length === 0) {
            return;
        }

        orderUpdateMutation.mutate(
            {
                orderId,
                commands: pendingOrderUpdateCommands,
            },
            {
                onSuccess: () => {
                    clearPendingSubmissions();
                    exitAssetDetailsEditMode();
                    getNotifier()?.add({
                        title: translate('asset_review_changes_save_success_title'),
                        message: translate('asset_review_changes_save_success_body'),
                        type: 'info',
                    });
                    onClose();
                },
                onError: (error) => {
                    getNotifier()?.add({
                        title: translate('asset_review_changes_save_error_title'),
                        message: translate('asset_review_changes_save_error_body'),
                        type: 'error',
                    });
                    logError(error);
                },
            }
        );
    };

    return (
        <Modal handleCloseBtn={onClose} visible={visible} data-testid="asset-review-changes-modal">
            <Sheets
                aria-label={title}
                header={<ModalHeader title={title} />}
                footer={
                    <ModalFooter>
                        <Button
                            type="secondary"
                            text={translate('asset_details_mode_discard')}
                            disabled={orderUpdateMutation.isPending}
                            onClick={handleDiscard}
                        />
                        <Button
                            type="primary"
                            text={translate('save')}
                            disabled={!orderId || pendingOrderUpdateCommands.length === 0}
                            loading={orderUpdateMutation.isPending}
                            onClick={submitPendingChanges}
                        />
                    </ModalFooter>
                }
            >
                <ModalBody>
                    <ul className={styles.changes} data-testid="asset-review-changes-list">
                        {pendingSubmissionSummaries.map((summary) => (
                            <li key={summary.id} className={styles.change}>
                                <Text tagName="h2" textStyle="Heading.XS">
                                    {summary.label}
                                </Text>
                                <dl className={styles.change_values}>
                                    <div>
                                        <dt>
                                            <Text tagName="span" textStyle="Body.S">
                                                {translate('asset_review_changes_before')}
                                            </Text>
                                        </dt>
                                        <dd>
                                            <Text tagName="span" textStyle="Body.M">
                                                {summary.before}
                                            </Text>
                                        </dd>
                                    </div>
                                    <div>
                                        <dt>
                                            <Text tagName="span" textStyle="Body.S">
                                                {translate('asset_review_changes_after')}
                                            </Text>
                                        </dt>
                                        <dd>
                                            <Text tagName="span" textStyle="Body.M">
                                                {summary.after}
                                            </Text>
                                        </dd>
                                    </div>
                                </dl>
                            </li>
                        ))}
                    </ul>
                </ModalBody>
            </Sheets>
        </Modal>
    );
};
